import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { GOLD } from '../../theme/tokens';

const audiences = [
    { icon: 'bi-mortarboard-fill', title: 'Educators', text: 'With TC Everywhere, educators can craft lesson plans that direct students to high-quality online resources, using tracking and assessment features to gauge comprehension.' },
    { icon: 'bi-building', title: 'Employers', text: 'Instead of simply disseminating policies via a wiki, employers can use TC Everywhere to commend employees who engage with the material and confirm their understanding.' },
    { icon: 'bi-search', title: 'Recruiters', text: "Recruiters can set up challenges that showcase candidates' abilities before any face-to-face meetings occur — streamlining the recruitment process." },
    { icon: 'bi-camera-video-fill', title: 'Content Creators', text: 'By incorporating questions at strategic points within videos or through short courses, creators can transform passive viewers into an active, recognized audience.' },
];

const issuerTypes = [
    'Private Individuals', 'Teachers & Professors', 'YouTube Channels & Publishers',
    'Schools & Universities', 'Corporations', 'Non-Profit Organizations',
    'Government Entities', 'Online Learning Platforms',
];

export default function AudiencePage() {
    return (
        <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 10, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" fontWeight={700} gutterBottom>Who Is TC Everywhere For?</Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>
                        TC Everywhere is designed to serve two primary audiences: <strong>Issuers</strong> and <strong>Earners</strong>.
                    </Typography>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Grid container alignItems="center" spacing={6} mb={8}>
                        <Grid item xs={12} lg={6}>
                            <Box sx={{ bgcolor: 'primary.main', color: 'white', display: 'inline-flex', px: 2, py: 0.5, borderRadius: 1, mb: 2, fontSize: '0.875rem' }}>For Issuers</Box>
                            <Typography variant="h4" fontWeight={700} gutterBottom>Anyone Who Recognizes Achievement</Typography>
                            <Typography color="text.secondary" paragraph>
                                Our platform accommodates a diverse range of individuals and organizations. Whether
                                you're a private individual, an educator, a content creator, or part of a large
                                institution — TC Everywhere is tailored to suit your needs.
                            </Typography>
                            <Grid container spacing={1}>
                                {issuerTypes.map(type => (
                                    <Grid item xs={12} sm={6} key={type}>
                                        <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                                            <Box component="i" className="bi bi-check-circle-fill" sx={{ color: 'success.main', flexShrink: 0, fontSize: '0.875rem' }} />
                                            <Typography variant="body2">{type}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={6} sx={{ textAlign: 'center' }}>
                            <Box component="i" className="bi bi-people-fill" sx={{ fontSize: '9rem', color: 'primary.main', opacity: 0.1 }} />
                        </Grid>
                    </Grid>

                    <Grid container alignItems="center" spacing={6} flexDirection={{ xs: 'column', lg: 'row-reverse' }}>
                        <Grid item xs={12} lg={6}>
                            <Box sx={{ bgcolor: GOLD, color: 'black', display: 'inline-flex', px: 2, py: 0.5, borderRadius: 1, mb: 2, fontSize: '0.875rem', fontWeight: 600 }}>For Earners</Box>
                            <Typography variant="h4" fontWeight={700} gutterBottom>Anyone Who Wants to Learn and Grow</Typography>
                            <Typography color="text.secondary" paragraph>
                                TC Everywhere is inclusive of anyone who utilizes the internet as a tool for learning
                                and personal growth. Whether you're a student, an employee seeking to enhance your
                                skills, or simply someone eager to expand your knowledge.
                            </Typography>
                            <Button component={Link} to="/available-badges" variant="contained" sx={{ mr: 2 }}>Browse Available Badges</Button>
                            <Button component={Link} to="/register" variant="outlined">Create Account</Button>
                        </Grid>
                        <Grid item xs={12} lg={6} sx={{ textAlign: 'center' }}>
                            <Box component="i" className="bi bi-trophy-fill" sx={{ fontSize: '9rem', color: 'accent.main', opacity: 0.15 }} />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ py: 8, bgcolor: 'background.paper' }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Real-World Use Cases</Typography>
                    <Typography color="text.secondary" textAlign="center" mb={6}>These are just a few of the diverse applications for TC Everywhere.</Typography>
                    <Grid container spacing={4}>
                        {audiences.map(({ icon, title, text }) => (
                            <Grid item xs={12} md={6} key={title}>
                                <Card sx={{ height: '100%', p: 2 }}>
                                    <CardContent>
                                        <Box display="flex" alignItems="flex-start" gap={2}>
                                            <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 1, p: 1.5, flexShrink: 0 }}>
                                                <Box component="i" className={`bi ${icon}`} sx={{ fontSize: '1.25rem' }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight={700} gutterBottom>{title}</Typography>
                                                <Typography variant="body2" color="text.secondary">{text}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h4" fontWeight={700} gutterBottom>Find yourself in here?</Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.85, mb: 4 }}>Get started for free — no commitment, no credit card.</Typography>
                    <Button component={Link} to="/register" variant="contained" color="secondary" size="large" sx={{ fontWeight: 700 }}>
                        Join TC Everywhere
                    </Button>
                </Container>
            </Box>
        </>
    );
}
